import {
  canonicalLabel,
  COLLECTION_IMPORT_FIELDS,
  ImportFieldDef,
} from './import-schema';

/** Αντιστοιχισμένο πεδίο μετά την ανάγνωση επικεφαλίδας */
export type MappedImportField = {
  def: ImportFieldDef;
  colIdx: number;
  matchedLabel?: string;
  colValue?: unknown;
  fieldValue?: unknown;
};

export type CodeColumnStrategy = 'single' | 'split';

export type ImportFieldMapState = {
  fields: Record<string, MappedImportField>;
  codeStrategy: CodeColumnStrategy | null;
};

/** Κανονικοποίηση κειμένου επικεφαλίδας για σύγκριση */
export function normalizeHeaderLabel(value: unknown): string {
  if (value == null) return '';
  return String(value)
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '');
}

/** Βρίσκει δείκτη στήλης που ταιριάζει σε κάποιο alias */
function findColumnIndex(headerRow: unknown[], labels: string[]): { idx: number; label?: string } {
  const normalizedHeaders = headerRow.map(normalizeHeaderLabel);
  for (const label of labels) {
    const normalizedLabel = normalizeHeaderLabel(label);
    const idx = normalizedHeaders.findIndex((h) => h === normalizedLabel);
    if (idx >= 0) return { idx, label };
  }
  return { idx: -1 };
}

/** Επιστρέφει στρατηγική κωδικού εντύπου από τις στήλες που βρέθηκαν */
function detectCodeStrategy(fields: Record<string, MappedImportField>): CodeColumnStrategy | null {
  const hasCode = fields.code?.colIdx >= 0;
  const hasSplit = fields.code_01?.colIdx >= 0 && fields.code_02?.colIdx >= 0;
  if (hasCode) return 'single';
  if (hasSplit) return 'split';
  return null;
}

/**
 * Αντιστοιχίζει επικεφαλίδες Excel στα πεδία schema.
 * Πετάει Error αν λείπουν υποχρεωτικά πεδία ή δεν υπάρχει τρόπος κωδικού εντύπου.
 */
export function mapHeaderToFields(headerRow: unknown[]): ImportFieldMapState {
  const fields: Record<string, MappedImportField> = {};

  for (const def of COLLECTION_IMPORT_FIELDS) {
    const { idx, label } = findColumnIndex(headerRow, def.labels);
    fields[def.field] = { def, colIdx: idx, matchedLabel: label };
  }

  const missingRequired = COLLECTION_IMPORT_FIELDS.filter(
    (d) => d.required && fields[d.field].colIdx < 0,
  ).map((d) => canonicalLabel(d));

  if (missingRequired.length) {
    throw new Error(`Λείπουν οι υποχρεωτικές στήλες: ${missingRequired.join(', ')}`);
  }

  const codeStrategy = detectCodeStrategy(fields);
  if (!codeStrategy) {
    throw new Error(
      'Απαιτείται είτε η στήλη «Κωδικός Εντύπου» είτε οι στήλες «Σειρά Παραστατικού» και «Αριθμ. Παραστατικού»',
    );
  }

  return { fields, codeStrategy };
}

/** Μηδενίζει τιμές γραμμής πριν την επεξεργασία νέας γραμμής */
export function resetRowFieldValues(state: ImportFieldMapState): void {
  for (const mapped of Object.values(state.fields)) {
    mapped.colValue = undefined;
    mapped.fieldValue = undefined;
  }
}

/** Επιλύει τον κωδικό εντύπου από τη γραμμή δεδομένων */
export function resolveCollectionCode(
  state: ImportFieldMapState,
  row: unknown[],
): string | null {
  const readCell = (field: string) => {
    const mapped = state.fields[field];
    if (!mapped || mapped.colIdx < 0) return undefined;
    return row[mapped.colIdx];
  };

  if (state.codeStrategy === 'single') {
    const value = readCell('code');
    if (value == null || String(value).trim() === '') return null;
    return String(value).trim();
  }

  const part1 = readCell('code_01');
  const part2 = readCell('code_02');
  if (part1 == null && part2 == null) return null;
  if (part1 == null && part2 != null) return String(part2).trim();
  if (part1 != null && part2 == null) return String(part1).trim();
  return `${part1} ${part2}`.trim();
}

/** Ανακτά resolved τιμή πεδίου μετά την επεξεργασία γραμμής */
export function getMappedFieldValue(
  state: ImportFieldMapState,
  field: string,
): unknown {
  return state.fields[field]?.fieldValue;
}

/** Ανακτά raw τιμή κελιού γραμμής */
export function getMappedCellValue(
  state: ImportFieldMapState,
  field: string,
  row: unknown[],
): unknown {
  const mapped = state.fields[field];
  if (!mapped || mapped.colIdx < 0) return undefined;
  return row[mapped.colIdx];
}

/** Πεδία δεδομένων που επεξεργάζονται ανά γραμμή (εκτός κωδικού εντύπου) */
export function dataFieldsForProcessing(state: ImportFieldMapState): MappedImportField[] {
  const skip = new Set(['code', 'code_01', 'code_02']);
  return Object.values(state.fields).filter((m) => m.colIdx >= 0 && !skip.has(m.def.field));
}
